import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowLeft, ArrowRight, Calendar, MapPin } from "lucide-react";
import { vacancies } from "@/data/vacancies";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formRateLimiter } from "@/lib/utils/rateLimit";
import { usePageTitle } from "@/hooks/usePageTitle";
interface ApplicationFormData {
  name: string;
  email: string;
  phone: string;
  cover_letter: string;
  resume: File | null;
}

const initialFormData: ApplicationFormData = {
  name: "",
  email: "",
  phone: "",
  cover_letter: "",
  resume: null
};

const VacancyDetailPage = () => {
  const { vacancyId } = useParams();
  const vacancy = vacancies.find((v) => v.id === vacancyId);
  usePageTitle(vacancy?.title || "Вакансия не найдена");
  const [formData, setFormData] = useState<ApplicationFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const process = [
      {
        title: "Отправка резюме",
        description: "Заполните форму или отправьте своё резюме на почту info@stackadvance.io"
      },
      {
        title: "Первое интервью",
        description: "Короткий звонок с HR для знакомства и обсуждения опыта"
      },
      {
        title: "Техническое интервью",
        description: "Разговор с техническим специалистом, обсуждение проектов, небольшое тестовое задание"
      },
      {
        title: "Финальное интервью",
        description: "Встреча с руководителем команды для обсуждения условий сотрудничества"
      },
      {
        title: "Предложение о работе",
        description: "В случае успешного прохождения всех этапов, мы направим вам оффер"
      }
    ]

  // For demo purposes - in real app would fetch data based on vacancyId

  if (!vacancy) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-20">
          <Container>
            <div className="py-32 text-center">
              <h1 className="text-3xl font-bold mb-6">Вакансия не найдена</h1>
              <Link to="/vacancies" className="button-primary">
                Вернуться к списку вакансий
              </Link>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, resume: file }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vacancyId) {
      toast.error("Ошибка: ID вакансии не найден");
      return;
    }

    // Check rate limiting
    if (!formRateLimiter.canSubmit("vacancy_application")) {
      const time = formRateLimiter.getRemainingTime("vacancy_application");
      setRemainingTime(time);
      toast.error(`Пожалуйста, подождите ${Math.ceil(time / 1000)} секунд перед повторной отправкой`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      await api.vacancyApplications.submit(vacancyId, {
        ...formData,
        vacancy_title: vacancy.title
      });
      toast.success("Ваша заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.");
      setIsSubmitted(true);
      setFormData(initialFormData);
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error: any) {
      console.error("Error submitting application:", error);
      toast.error(error.message || "Произошла ошибка при отправке заявки. Пожалуйста, попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <Link to="/vacancies" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Назад к вакансиям
            </Link>
            
            <div className="max-w-4xl mx-auto">
              <div className="tag mb-4 animate-fade-in">{vacancy.type}</div>
              <h1 className="heading text-3xl md:text-4xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                {vacancy.title}
              </h1>
              
              <div className="flex flex-wrap items-center text-muted-foreground mb-8 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                <div className="flex items-center mr-6 mb-2">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>{vacancy.location}</span>
                </div>
                <div className="flex items-center mb-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Опубликовано: {vacancy.date}</span>
                </div>
                {vacancy.salaryRange && (
                  <div className="ml-0 lg:ml-6 mb-2">
                    <span className="bg-secondary/70 px-3 py-1 rounded-full text-sm">{vacancy.salaryRange}</span>
                  </div>
                )}
              </div>
            </div>
          </Container>
        </section>

        {/* Vacancy Content */}
        <section className="py-10">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <div className="prose prose-lg max-w-none">
                  <h2 className="text-2xl font-bold mb-4">Описание вакансии</h2>
                  <p className="mb-4">{vacancy.description}</p>
                  
                  <h2 className="text-2xl font-bold mb-4">Обязанности</h2>
                  <div className="prose mb-8" dangerouslySetInnerHTML={{ __html: vacancy.responsibilities }} />
                  
                  <h2 className="text-2xl font-bold mb-4">Требования</h2>
                  <div className="prose mb-8" dangerouslySetInnerHTML={{ __html: vacancy.experience }} />
                  
                  <h2 className="text-2xl font-bold mb-4">Что мы предлагаем</h2>
                  <div className="prose" dangerouslySetInnerHTML={{ __html: vacancy.benefits }} />
                </div>
                
                <div className="mt-10 pt-6 border-t border-border">
                  <h3 className="text-xl font-bold mb-6">
                    Как проходит процесс отбора
                  </h3>
                  <div className="relative">
                    {/* Process timeline */}
                    <div className="hidden sm:block absolute left-8 inset-y-0 w-0.5 bg-border"></div>
                    
                    <div className="space-y-8">
                      {process.map((step, index) => (
                        <div key={index} className="relative flex items-start">
                          <div className="hidden sm:flex items-center justify-center h-16 w-16 rounded-full bg-secondary/70 text-foreground font-bold text-lg">
                            {index + 1}
                          </div>
                          <div className="flex sm:hidden items-center justify-center h-10 w-10 rounded-full bg-secondary/70 text-foreground font-bold text-lg mr-4">
                            {index + 1}
                          </div>
                          <div className="ml-0 sm:ml-6 pt-1">
                            <h4 className="text-lg font-semibold mb-1">{step.title}</h4>
                            <p className="text-muted-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-4">
                <div className="sticky top-24">
                  <div className="glass rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4">Откликнуться на вакансию</h3>
                    <p className="text-muted-foreground mb-6">Отправьте нам своё резюме, и мы свяжемся с вами в ближайшее время</p>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Имя</label>
                        <input 
                          type="text" 
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          placeholder="Ваше имя"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <input 
                          type="email" 
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Телефон</label>
                        <input 
                          type="tel" 
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          placeholder="+375 (XX) XXX-XX-XX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Сопроводительное письмо</label>
                        <textarea 
                          name="cover_letter"
                          value={formData.cover_letter}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[100px]"
                          placeholder="Расскажите коротко о себе"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Прикрепить резюме</label>
                        <div className="border border-dashed border-border rounded-md p-4 text-center">
                          <input 
                            type="file"
                            className="hidden"
                            id="resume"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx"
                          />
                          <label htmlFor="resume" className="cursor-pointer text-primary hover:text-primary/80">
                            {formData.resume ? formData.resume.name : "Выбрать файл"}
                          </label>
                        </div>
                      </div>
                      <button 
                        type="submit" 
                        className="button-primary w-full"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Отправка..." : "Отправить заявку"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </button>
                    </form>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-border mt-6">
                    <h3 className="text-xl font-semibold mb-4">Контакты HR</h3>
                    <p className="text-muted-foreground mb-2">
                      Если у вас есть вопросы по вакансии, вы можете связаться напрямую с нашим HR-специалистом
                    </p>
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-secondary/70 mr-3 flex items-center justify-center">
                        <span className="font-medium">HR</span>
                      </div>
                      <div>
                        <p className="font-medium">Александр</p>
                        <p className="text-sm text-muted-foreground">HR-менеджер</p>
                      </div>
                    </div>
                    <div className="pt-3 mt-3 border-t border-border">
                      <p className="flex items-center mb-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                          <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                        </svg>
                        <a href="mailto:info@stackadvance.io" className="text-primary hover:underline">info@stackadvance.io</a>
                      </p>
                      <p className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <a href="tel:+375293326012" className="text-primary hover:underline">+375 (29) 332-60-12</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Other Vacancies Section */}
        {(() => {
          const { vacancyId } = useParams();
          const otherVacancies = vacancies
            .filter(vacancy => vacancy.id !== vacancyId)
            .slice(0, 3);

          if (otherVacancies.length === 0) return null;

          return (
            <section className="py-20 bg-secondary">
              <Container>
                <div className="text-center mb-12">
                  <h2 className="heading text-3xl font-bold mb-4">
                    Другие вакансии
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Ознакомьтесь с другими открытыми позициями в нашей компании
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {otherVacancies.map(vacancy => (
                    <div key={vacancy.id} className="bg-white rounded-2xl p-6 border border-border hover:border-primary/20 hover:shadow-md transition-all">
                      <h3 className="text-xl font-semibold mb-2">{vacancy.title}</h3>
                      <div className="flex items-center text-muted-foreground mb-3">
                        <MapPin className="h-4 w-4 mr-1" /> 
                        <span>{vacancy.location}</span>
                      </div>
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {vacancy.description}
                      </p>
                      <Link to={`/vacancies/${vacancy.id}`} className="inline-flex items-center text-primary font-medium">
                        Подробнее 
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-10">
                  <Link to="/vacancies" className="button-secondary">
                    Смотреть все вакансии
                  </Link>
                </div>
              </Container>
            </section>
          );
        })()}
      </main>
      <Footer />
    </div>
  );
};

export default VacancyDetailPage;
